// Single source of truth for the capability pages under /funcionalidades. The
// header's desktop dropdown, its mobile accordion, the /funcionalidades hub
// and (from plan 15-06) the home page's teaser section all derive their
// entries from this ONE array — no consumer hardcodes its own slug list, so a
// partial or drifting listing across surfaces is structurally impossible.
//
// Array order IS presentation order everywhere this constant is consumed.
//
// This plan (15-01, the phase's tracer slice) seeds the array with exactly
// ONE entry, transcripcion-de-sesiones. Plans 15-02 through 15-04 each append
// their entry in the SAME commit as the page it points at, so the constant
// never references a route that does not exist yet (FEAT-04).

export interface EntradaFuncionalidad {
    /** Lowercase ASCII, accents folded, words hyphenated — matches /^[a-z0-9-]+$/. */
    slug: string;
    /** Short Label-role name: the capability, not the outcome (D-01). Used by the dropdown and the breadcrumb. */
    etiqueta: string;
    /** One-line outcome sentence the hub renders under `etiqueta`. */
    descripcion: string;
    /**
     * Optional explainer-video metadata (VID-05, D-07). An OBJECT, never an
     * array — exactly one video per capability page by construction. Phase 18
     * publishes the 2.0 explainers (D-06, D-08): each block is the one
     * `publicar-en-psicoguia.mjs` prints after `verificar-mp4`, so the files are
     * the verified renders and `duracionSegundos` is ffprobe on the published
     * MP4. The page renders the video section and construirGrafoVideo emits the
     * VideoObject only when the field exists; an entry without it shows
     * nothing. `ruta`/`poster` must resolve to real files under `public/video/`;
     * the `.vtt` captions path is derived from `slug` by convention
     * (`public/video/<slug>.vtt`).
     */
    video?: {
        /** Path under `public/`, e.g. "/video/<slug>.mp4". */
        ruta: string;
        /** Path under `public/`, e.g. "/video/<slug>-poster.jpg". */
        poster: string;
        /** Measured render duration, never an estimate. */
        duracionSegundos: number;
        ancho: number;
        alto: number;
    };
}

// Orden aprobado por el operador el 2026-09-03 (ver
// .planning/todos/pending/recorrido-paso-a-paso-entre-funcionalidades.md):
// el orden del array pasa a ser el orden del flujo de trabajo real dentro de
// la app, no el orden comercial anterior. Como el comentario de arriba deja
// dicho, ese orden ES el orden de presentación en todas partes — el
// desplegable, el hub y el teaser del home lo heredan sin tocar código propio.
// D-12 (Fase 17): las dos páginas nuevas entran en el orden del flujo del lienzo. Su
// `etiqueta` es el `nombre` del registro (paginasFuncionalidad.ts) y su `descripcion`, la
// primera frase del subtítulo, para que el desplegable y la página digan lo mismo.
export const FUNCIONALIDADES_NAV: EntradaFuncionalidad[] = [
    {
        slug: 'tablero-del-paciente',
        etiqueta: 'Tablero del paciente',
        descripcion: 'Abres la app y ves qué paciente necesita algo.',
        video: {
            ruta: '/video/tablero-del-paciente.mp4',
            poster: '/video/tablero-del-paciente-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (tablero-del-paciente.mp4: 46.366667 s), nunca una estimación.
            duracionSegundos: 46.37,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'documentos-del-paciente',
        etiqueta: 'Documentos del paciente',
        descripcion: 'Sube historiales antiguos en PDF y ten una visión completa desde el primer día.',
        video: {
            ruta: '/video/documentos-del-paciente.mp4',
            poster: '/video/documentos-del-paciente-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (documentos-del-paciente.mp4: 43.433333 s), nunca una estimación.
            duracionSegundos: 43.43,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'tests-psicometricos',
        etiqueta: 'Tests psicométricos',
        // 17-29 (auditoría SEO 2.0, contenido H-2): la frase anterior a la 2.0 no decía el modelo de D-23.
        descripcion: 'Subes la prueba ya corregida, confirmas sus puntuaciones y ves cómo evoluciona cada escala.',
        video: {
            ruta: '/video/tests-psicometricos.mp4',
            poster: '/video/tests-psicometricos-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (tests-psicometricos.mp4: 42.233333 s), nunca una estimación.
            duracionSegundos: 42.23,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'transcripcion-de-sesiones',
        etiqueta: 'Transcripción de sesiones',
        descripcion: 'De la grabación a un texto listo para revisar, sin escribir una palabra.',
        video: {
            ruta: '/video/transcripcion-de-sesiones.mp4',
            poster: '/video/transcripcion-de-sesiones-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (transcripcion-de-sesiones.mp4: 42.433333 s), nunca una estimación.
            duracionSegundos: 42.43,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'analisis-clinico-dsm-5',
        etiqueta: 'Análisis clínico y DSM-5',
        // La referencia DSM-5 la responde un modelo desde su propio conocimiento: NO es
        // una búsqueda sobre un manual almacenado ni indexado. La redacción anterior
        // ("consulta el manual DSM-5") prometía un cotejo documental que el producto no
        // hace — ver CLM-06 en la fase 17.
        descripcion: 'Ten los criterios diagnósticos a mano mientras registras tu propia hipótesis.',
        video: {
            ruta: '/video/analisis-clinico-dsm-5.mp4',
            poster: '/video/analisis-clinico-dsm-5-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (analisis-clinico-dsm-5.mp4: 43.666667 s), nunca una estimación.
            duracionSegundos: 43.67,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'notas-de-progreso',
        etiqueta: 'Notas de progreso',
        // Cinco formatos, no cuatro: el enum de progressNote.model.js incluye GIRP, que
        // se enviaba en producción sin anunciarse — ver CLM-03 en la fase 17.
        descripcion: 'Un borrador SOAP, DAP, BIRP, GIRP o PIRP listo para revisar después de cada sesión.',
        video: {
            ruta: '/video/notas-de-progreso.mp4',
            poster: '/video/notas-de-progreso-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (notas-de-progreso.mp4: 37.166667 s), nunca una estimación.
            duracionSegundos: 37.17,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'plan-de-tratamiento',
        etiqueta: 'Plan de tratamiento',
        // 17-29: la frase anterior a la 2.0 no decía que los cambios son propuestas que el terapeuta aprueba.
        descripcion: 'Objetivos con indicador, tareas entre sesiones y propuestas que tú apruebas.',
        video: {
            ruta: '/video/plan-de-tratamiento.mp4',
            poster: '/video/plan-de-tratamiento-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (plan-de-tratamiento.mp4: 43.9 s), nunca una estimación.
            duracionSegundos: 43.9,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'informes-y-plantillas-clinicas',
        etiqueta: 'Informes y plantillas clínicas',
        descripcion:
            'Pides una historia clínica o un informe de seguimiento y el asistente lo redacta sección a sección a partir del caso.',
        video: {
            ruta: '/video/informes-y-plantillas-clinicas.mp4',
            poster: '/video/informes-y-plantillas-clinicas-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (informes-y-plantillas-clinicas.mp4: 42.033333 s), nunca una estimación.
            duracionSegundos: 42.03,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'chatbot-con-historial-clinico',
        // D-12/D-13 (fase 17): la página del chat pasa a ser «Poderes del chat»;
        // el slug se conserva para no romper enlaces entrantes ni el sitemap.
        etiqueta: 'Poderes del chat',
        descripcion: 'Pregúntale al historial completo del paciente, no solo a la sesión de hoy.',
        video: {
            ruta: '/video/chatbot-con-historial-clinico.mp4',
            poster: '/video/chatbot-con-historial-clinico-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (chatbot-con-historial-clinico.mp4: 44.633333 s), nunca una estimación.
            duracionSegundos: 44.63,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'organizaciones-y-equipos',
        etiqueta: 'Organizaciones y equipos',
        // CLM-01 en fase 17: la organización comparte licencias y facturación, no el
        // historial clínico. Cada terapeuta conserva sus propios pacientes en privado.
        descripcion: 'Invita a tu equipo y gestiona licencias y facturación, sin compartir el historial clínico de nadie.',
        video: {
            ruta: '/video/organizaciones-y-equipos.mp4',
            poster: '/video/organizaciones-y-equipos-poster.jpg',
            // Medido con ffprobe sobre el MP4 publicado (organizaciones-y-equipos.mp4: 45.3 s), nunca una estimación.
            duracionSegundos: 45.3,
            ancho: 1920,
            alto: 1080,
        },
    },
];
